/**
 * Service Worker cleanup plugin — shared across all apps (gokollab, meet,
 * gokollab-meet, courses, clientportal).
 *
 * Why: users who visited the legacy `ghl-revex-frontend` monorepo still have
 * its Service Workers registered in their browser. Those SWs can intercept
 * bundle fetches and cache bad responses, trapping users on 522/5xx errors
 * even after they migrate to the new `ghl-membership-frontend` app.
 *
 * Strategy: explicit denylist of known-legacy SW filenames. We only
 * unregister SWs whose script URL path matches one of these names. Current FCM
 * uses `cp-firebase-messaging-sw.js` (see push-notifications-layer) — not on this list.
 *
 * Legacy SW filenames were enumerated from the `ghl-revex-frontend` route
 * graph via ghl-ai codebase-memory. If new legacy names appear, extend this
 * list in one place and it applies to every app.
 *
 * All failures are swallowed — the plugin must never break the app.
 */

const LEGACY_SW_FILENAMES: string[] = [
  'clientclub-sw.js',
  'clientclub-firebase-sw.js',
  'gokollab-firebase-sw.js',
  'main-sw.js',
  'firebase-messaging-sw.js',
]

// Cache Storage keys the legacy SWs are known to create.
const LEGACY_CACHE_KEYS = new Set(['file-cache'])

function isLegacyScriptUrl(url: string): boolean {
  if (!url) return false
  return LEGACY_SW_FILENAMES.some(name => url.endsWith(`/${name}`))
}

function shouldUnregister(reg: ServiceWorkerRegistration): boolean {
  const url = reg.active?.scriptURL ?? reg.waiting?.scriptURL ?? reg.installing?.scriptURL ?? ''
  return isLegacyScriptUrl(url)
}

export async function runSwCleanup(win = globalThis): Promise<void> {
  if (!win) return
  if (!('serviceWorker' in win.navigator)) return

  try {
    const registrations = await win.navigator.serviceWorker.getRegistrations()
    const targets = registrations.filter(shouldUnregister)
    await Promise.all(targets.map(r => r.unregister().catch(() => false)))
  } catch (err) {
    console.warn('[sw-cleanup] Failed to audit service worker registrations', err)
  }

  try {
    if (!('caches' in win)) return
    const keys = await win.caches.keys()
    const targets = keys.filter(k => LEGACY_CACHE_KEYS.has(k))
    await Promise.all(targets.map(k => win.caches.delete(k).catch(() => false)))
  } catch (err) {
    console.warn('[sw-cleanup] Failed to clear legacy cache storage', err)
  }
}

export default defineNuxtPlugin(async () => {
  // Block app boot until legacy SWs and caches are cleared so they cannot intercept bundle fetches.
  await runSwCleanup()
})
